//// filepath: /d:/NwN/content-creation-frontend/src/components/Modals/ThumbnailSelection.jsx
import { useState, useRef, useEffect } from "react";

//mui
import { styled } from "@mui/material/styles";
import { Dialog, Typography, Checkbox, CircularProgress } from "@mui/material";

//css
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/navigation";

//icons
import { ReactComponent as UncheckedIcon } from "../../assets/unchecked.svg";
import { ReactComponent as CheckedIcon } from "../../assets/checked.svg";
import { ReactComponent as PublishIcon } from "../../assets/publish_icon.svg";

// hooks
import useWindowDimensions from "../../customHooks/useWindowDimensions";

//components
import PreviewImageVideo from "./PreviewImageVideo";
import CustomSwiper from "../Swiper/CustomSwiper";
import CustomButton from "../Common/CustomButton";

// Reuse PublishContentDialog-like styling:
const Container = styled("div")(({ theme }) => ({
  // padding: "1rem",
  "& .thumbnail_section": {
    margin: "0.75rem 1rem",
  },
  "& .thumbnail_label": {
    color: "#303030",
    fontSize: "14px",
    fontWeight: 500,
  },
  "& .heading": {
    margin: "0rem 1rem",
    padding: "0.75rem 0",
    borderBottom: "1px solid #EAEAEA",
    "& .text_style": {
      fontWeight: "600",
      fontSize: "18px",
    },
  },
  "& .content": {
    marginTop: "0.5rem",
    // border: "1px solid #E5E5E5",
    display: "flex",
    height: "fit-content",
  },
  "& .block": {
    position: "relative",
    width: "fit-content",
    "& .items": {
      width: "200px",
      height: "125px",
      border: "1px solid #E5E5E5",
      borderRadius: "4px",
      position: "relative",
      cursor: "pointer",
      "& .img_style": {
        width: "100%",
        height: "100%",
        borderRadius: "4px",
        objectFit: "cover",
        opacity: 0.8,
      },
      "& .preview_text": {
        position: "absolute",
        color: "#FFFFFF",
        fontSize: "12px",
        fontWeight: 500,
        left: "50%",
        top: "50%",
        transform: "translate(-50%,-50%)",
        visibility: "hidden",
        cursor: "pointer",
      },
      "&:hover .img_style:hover": {
        backgroundColor: "#000000",
        filter: "brightness(80%)",
        opacity: 0.4,
      },
      "&:hover .preview_text": {
        visibility: "visible",
      },
    },
    "& .selected": {
      border: "4px solid #16F529",
    },
    ".css-1tbh9pm-MuiButtonBase-root-MuiCheckbox-root": {
      padding: "5px",
    },
    "& .check_style": {
      position: "absolute",
      top: "2px",
      left: "2px",
      zIndex: 2,
    },
  },
  "& .actions": {
    boxShadow: "0px 0px 12px 0px #0000001F",
    padding: "0.5rem 1rem",
    display: "flex",
    justifyContent: "flex-end",
    marginTop: "1rem",
    "& .back_btn": {
      marginRight: "1rem",
    },
  },
  "& .thumbnail_stop_block": {
    padding: "1rem 0",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
}));

const ThumbnailSelection = ({
  open,
  onClose,
  userUploadedThumbnails = [],
  videoFile,
  thumbnailUrl = "",
  handleCheckBoxClick,
  handlePublish,
}) => {
  const [openPreview, setOpenPreview] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [thumbnailType, setThumbnailType] = useState("");
  const [selectedPreview, setSelectedPreview] = useState(null);
  const [generatedThumbnails, setGeneratedThumbnails] = useState([]);
  const { width } = useWindowDimensions();
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const cancelGenerationRef = useRef(false);

  const generateThumbnailsInBatches = async (
    videoUrl,
    chunkSize = 10,
    intervalInSeconds = 1
  ) => {
    try {
      setIsGenerating(true);
      setGeneratedThumbnails([]); // clear old frames if desired
      cancelGenerationRef.current = false;

      const video = videoRef.current;
      video.src = videoUrl;
      await new Promise((resolve) => {
        video.onloadedmetadata = resolve;
      });

      const canvas = canvasRef.current;
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const context = canvas.getContext("2d");

      const framesArray = [];
      const framesSet = new Set();

      const totalDuration = Math.floor(video.duration);
      const allFrameTimes = [];

      // Populate frame times at the desired interval
      for (let time = 0; time < totalDuration; time += intervalInSeconds) {
        allFrameTimes.push(time);
      }

      for (let i = 0; i < allFrameTimes.length; i += chunkSize) {
        if (cancelGenerationRef.current) break;
        const chunkTimes = allFrameTimes.slice(i, i + chunkSize);

        for (const time of chunkTimes) {
          if (cancelGenerationRef.current) break;
          video.currentTime = time;
          await new Promise((resolve) => {
            video.onseeked = resolve;
          });

          context.drawImage(video, 0, 0, canvas.width, canvas.height);
          const dataUrl = canvas.toDataURL("image/jpeg", 1);
          if (!framesSet.has(dataUrl)) {
            framesSet.add(dataUrl);
            framesArray.push({
              file_urls: [dataUrl],
              pk: framesArray.length,
              subcategory: "video_frame",
            });
          }
        }
        setGeneratedThumbnails([...framesArray]);
        await new Promise((r) => setTimeout(r, 50));
      }

      setIsGenerating(false);
    } catch (error) {
      console.error("Error generating thumbnails in chunks:", error);
      setIsGenerating(false);
    }
  };

  const handlePreview = (item) => {
    if (item.subcategory === "thumbnail") {
      setThumbnailType("thumbnail");
    } else {
      setThumbnailType("video_frame");
    }
    setSelectedPreview(item?.pk);
    setOpenPreview(true);
  };

  useEffect(() => {
    // since the thumbnail generation operation is async we need to explicitly cancel it when the component unmounts
    return () => {
      cancelGenerationRef.current = true;
    };
  }, []);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth={
        width <= 1200 ? "sm" : width > 1200 && width <= 1536 ? "md" : "md"
      }
    >
      <Container>
        <div className="heading">
          <Typography className="text_style">Thumbnail Selection</Typography>
        </div>

        {/* USER-UPLOADED THUMBNAILS */}
        {userUploadedThumbnails.length !== 0 && (
          <div className="thumbnail_section">
            <Typography className="thumbnail_label">
              User-Uploaded Thumbnails
            </Typography>
            <div className="content">
              <CustomSwiper
                data={userUploadedThumbnails}
                renderItem={(item) => (
                  <div className="block">
                    <div
                      className={`items ${thumbnailUrl === item?.file_urls[0] ? "selected" : ""
                        }`}
                      onClick={() => handlePreview(item)}
                    >
                      <img
                        src={item.file_urls[0]}
                        alt="thumbnail"
                        className="img_style"
                      />
                      <Typography className="preview_text">Preview</Typography>
                    </div>
                    <Checkbox
                      className="check_style"
                      checked={thumbnailUrl === item?.file_urls[0]}
                      icon={<UncheckedIcon />}
                      checkedIcon={<CheckedIcon />}
                      color="secondary"
                      size="small"
                      onChange={(e) =>
                        handleCheckBoxClick(e, item, "thumbnail")
                      }
                    />
                  </div>
                )}
              />
            </div>
          </div>
        )}

        {/* GENERATED THUMBNAILS */}
        <div className="thumbnail_section">
          <Typography className="thumbnail_label">
            {isGenerating
              ? "Generating Thumbnails From Videos..."
              : "Choose Thumbnail From Video"}
            {isGenerating && generatedThumbnails.length !== 0 && (
              <CircularProgress size="1em" sx={{ marginLeft: "0.5rem" }} />
            )}
          </Typography>
          {generatedThumbnails.length === 0 ? (
            <div
              style={{
                marginTop: "1rem",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                // height: "125px",
              }}
            >
              {isGenerating ? (
                <CircularProgress />
              ) : (
                <CustomButton
                  variant="contained"
                  onClick={() =>
                    generateThumbnailsInBatches(videoFile?.file_urls[0], 10, 4)
                  }
                >
                  Generate Thumbnails
                </CustomButton>
              )}
            </div>
          ) : (
            <div className="content">
              <CustomSwiper
                data={generatedThumbnails}
                renderItem={(item, index) => (
                  <div className="block">
                    <div
                      className={`items ${thumbnailUrl === item?.file_urls[0] ? "selected" : ""
                        }`}
                      onClick={() => handlePreview(item)}
                    >
                      <img
                        src={item.file_urls?.[0]}
                        alt={`generated-thumb-${index}`}
                        className="img_style"
                      />
                      <Typography className="preview_text">Preview</Typography>
                    </div>
                    <Checkbox
                      className="check_style"
                      checked={thumbnailUrl === item?.file_urls[0]}
                      icon={<UncheckedIcon />}
                      checkedIcon={<CheckedIcon />}
                      color="secondary"
                      size="small"
                      onChange={(e) =>
                        handleCheckBoxClick(e, item, "thumbnail")
                      }
                    />
                  </div>
                )}
              />
            </div>
          )}

          {isGenerating ?
            <div className="thumbnail_stop_block">
              <CustomButton
                variant="contained"
                onClick={() => (cancelGenerationRef.current = true)}
              >
                Stop Thumbnails generation
              </CustomButton>
            </div> :
            <></>
          }

          {/* hidden elements for frame generation */}
          <video
            ref={videoRef}
            style={{ display: "none" }}
            crossOrigin="anonymous"
          />
          <canvas ref={canvasRef} style={{ display: "none" }} />
        </div>

        {/* ACTION BUTTONS */}
        <div className="actions">
          <CustomButton onClick={onClose} className="back_btn">
            Back
          </CustomButton>
          <CustomButton
            variant="contained"
            onClick={handlePublish}
            startIcon={<PublishIcon />}
          >
            Publish
          </CustomButton>
        </div>

        {/* Preview modal */}
        {openPreview && (
          <PreviewImageVideo
            open={openPreview}
            onClose={() => setOpenPreview(false)}
            selectedPreview={selectedPreview}
            totalAttachments={
              thumbnailType === "thumbnail"
                ? userUploadedThumbnails
                : generatedThumbnails
            }
            type={"image"}
          />
        )}
      </Container>
    </Dialog>
  );
};

export default ThumbnailSelection;
